import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, apiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);
  const [businesses, setBusinesses] = useState([]);
  const [activeBusiness, setActiveBiz] = useState(localStorage.getItem("billone_business") || "");

  const loadBusinesses = useCallback(async () => {
    try {
      const { data } = await api.get("/businesses");
      setBusinesses(data);
      const cur = localStorage.getItem("billone_business");
      if (!cur || !data.find((b) => b.id === cur)) {
        const first = data[0]?.id || "biz_main";
        localStorage.setItem("billone_business", first);
        setActiveBiz(first);
      }
      return data;
    } catch { return []; }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("billone_token");
    if (!token) { setUser(false); setReady(true); return; }
    api.get("/auth/me")
      .then(async (r) => { setUser(r.data); await loadBusinesses(); })
      .catch(() => { localStorage.removeItem("billone_token"); setUser(false); })
      .finally(() => setReady(true));
  }, [loadBusinesses]);

  async function setSession(token, u) {
    localStorage.setItem("billone_token", token);
    const first = u.business_ids?.[0] || "biz_main";
    localStorage.setItem("billone_business", first);
    setActiveBiz(first);
    setUser(u);
    await loadBusinesses();
  }

  async function login(email, password, loginType) {
    try {
      const { data } = await api.post("/auth/login", { email, password, login_type: loginType });
      await setSession(data.access_token, data.user);
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: apiError(e) };
    }
  }

  function switchBusiness(id) {
    localStorage.setItem("billone_business", id);
    setActiveBiz(id);
    window.location.reload();
  }

  function logout() {
    api.post("/auth/logout").catch(() => {});
    localStorage.removeItem("billone_token");
    localStorage.removeItem("billone_business");
    setUser(false);
  }

  function hasPerm(perm) {
    if (!user) return false;
    if (user.role === "super_admin") return true;
    return !!user.effective_permissions?.[perm];
  }

  const isSuperAdmin = user && user.role === "super_admin";

  return (
    <AuthContext.Provider value={{ user, ready, login, setSession, logout, hasPerm, isSuperAdmin, businesses, activeBusiness, switchBusiness, loadBusinesses }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
